import { useDispatch, useLoader, useSelector } from "@app/react";
import { securitySettingsUrl } from "@app/routes";
import { selectCurrentUser, updateUserName } from "@app/users";
import { existValidator, nameValidator, sanitizeInput } from "@app/validator";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useValidator } from "../hooks";
import {
  BannerMessages,
  Box,
  Button,
  CopyText,
  FormGroup,
  Group,
  IconInfo,
  Input,
  Switch,
  Tooltip,
  getCookie,
  tokens,
  writeCookie,
} from "../shared";

function AdditionalProfileSettings() {
  const [cachingEnabled, setCachingEnabled] = useState(
    getCookie("aptible-enable-local-storage-caching") === "true",
  );

  const handleCachingToggle = (enabled: boolean) => {
    setCachingEnabled(enabled);
    if (enabled) {
      writeCookie("aptible-enable-local-storage-caching", "true", 365);
    } else {
      // Set to false and expire immediately
      writeCookie("aptible-enable-local-storage-caching", "false", -1);
    }
    // Reload to apply changes
    window.location.reload();
  };

  return (
    <Box>
      <Group>
        <h3 className={tokens.type.h3}>Browser-side Settings</h3>
        <div>
          These settings apply only to your current browser, and will not affect
          other members of your organization, or other browsers you use.
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={cachingEnabled}
            onChange={handleCachingToggle}
            ariaLabel="Enable enhanced localStorage caching"
          >
            Enable Enhanced Caching
          </Switch>
          <Tooltip
            text={
              <div className="max-w-xs">
                This feature uses localStorage to cache apps, databases, and
                other Aptible resources to improve performance. Sensitive
                information (app ENV, connection URLs, passwords, certificates)
                are removed before storing data locally. Only select keys are
                cached.
              </div>
            }
            placement="right"
          >
            <IconInfo
              className="w-4 h-4 text-gray-500 cursor-help"
              title="Enchanced caching info"
            />
          </Tooltip>
        </div>
      </Group>
    </Box>
  );
}

export function SettingsProfilePage() {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const [name, setName] = useState(user.name);
  const loader = useLoader(updateUserName);
  const data = {
    userId: user.id,
    name,
  };
  const action = updateUserName(data);
  const validators = {
    name: (props: UpdateNameForm) =>
      existValidator(props.name, "Name") || nameValidator(props.name),
  };
  const [errors, validate] = useValidator<UpdateNameForm, typeof validators>(
    validators,
  );
  const isDisabled = name === user.name || !!errors.name;

  interface UpdateNameForm {
    name: string;
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isDisabled) {
      return;
    }

    if (!validate(data)) return;
    dispatch(action);
  };

  useEffect(() => {
    setName(user.name);
  }, [user.name]);

  return (
    <Group>
      <h2 className={tokens.type.h2}>Profile</h2>
      <Box>
        <Group size="sm" variant="horizontal">
          <Group size="sm" className="w-[180px] font-bold">
            <div>Name</div>
            <div>ID</div>
            <div>Email</div>
            <div>Password</div>
            <div>2-Factor Authentication</div>
            <div>Security Keys</div>
            <div>Log out other sessions</div>
          </Group>

          <Group size="sm">
            <div>{user.name}</div>
            <CopyText text={user.id} />
            <Group variant="horizontal">
              <CopyText text={user.email} />
              <Link to={securitySettingsUrl()}>Edit</Link>
            </Group>
            <Link to={securitySettingsUrl()}>Edit</Link>
            <Link to={securitySettingsUrl()}>Edit</Link>
            <Link to={securitySettingsUrl()}>Edit</Link>
            <Link to={securitySettingsUrl()}>Edit</Link>
          </Group>
        </Group>
      </Box>
      <Box>
        <form onSubmit={onSubmit}>
          <Group>
            <BannerMessages {...loader} />

            <FormGroup
              label="Name"
              htmlFor="name"
              feedbackMessage={errors.name}
              feedbackVariant={errors.name ? "danger" : "info"}
            >
              <Input
                type="text"
                id="name"
                name="name"
                value={name}
                onChange={(e) => {
                  setName(sanitizeInput(e.currentTarget.value));
                  validate({
                    name: sanitizeInput(e.currentTarget.value),
                  });
                }}
              />
            </FormGroup>

            <div>
              <Button
                type="submit"
                isLoading={loader.isLoading}
                disabled={isDisabled}
              >
                Save Changes
              </Button>
            </div>
          </Group>
        </form>
      </Box>
      <AdditionalProfileSettings />
    </Group>
  );
}
